/**
 * NoteTemplatesSection — R20-18 beta minimum
 *
 * Per Specifications/CATALOG_INVENTORY_AND_NOTE_TEMPLATES_SPEC.md § B1a:
 *   - CRUD: name + note (both required)
 *   - active/inactive per template
 *   - Hide Inactive / Show All switch (default: hide inactive)
 *   - Renders as the second section of the Templates page
 *
 * The line-item picker (spec § B1b: "insert-from-template at the note
 * field") lands in a follow-up commit that touches the quote builder.
 *
 * Data model — beta minimum + forward-compat:
 *   { id, name, note, active, tags? }
 *
 * The `tags` field is carried forward from day one (spec instruction) so
 * later expansion (§ B2a) is a mount, not a migration. Nothing writes it
 * in the beta.
 *
 * Storage: settingsAPI list_type='note_templates'.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Card, CardBody } from '../ui/Card';
import { SectionCardHeader } from '../ui/SectionCard';
import { Alert } from '../ui/Alert';
import { Slideover, useToast, useConfirm, Checkbox } from '../ui';
import { PlusIcon, PencilSquareIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { settingsAPI } from '../../services/api';

const emptyNoteTemplate = () => ({
  id: `nt-${Date.now()}`,
  name: '',
  note: '',
  active: true,
  tags: [],   // carried forward per spec § B2a; unused in beta
});

export default function NoteTemplatesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // null = closed; object = form data
  const [hideInactive, setHideInactive] = useState(true);
  const toast = useToast();
  const confirm = useConfirm();

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getList('note_templates');
      const arr = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setItems(arr);
    } catch (err) {
      setError('Failed to load note templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSave = async (item) => {
    // Validation: both name and note are REQUIRED per spec § B1a.
    if (!item.name.trim() || !item.note.trim()) {
      toast.error('Name and note are both required');
      return;
    }
    try {
      const isNew = !items.some((t) => t.id === item.id);
      if (isNew) {
        await settingsAPI.addListItem('note_templates', item);
        toast.success('Note template added');
      } else {
        await settingsAPI.updateListItem('note_templates', item.id, item);
        toast.success('Note template updated');
      }
      setEditing(null);
      await fetchItems();
    } catch (err) {
      toast.error('Failed to save note template');
    }
  };

  const handleDelete = async (item) => {
    const ok = await confirm({
      title: 'Delete note template?',
      message: `Delete "${item.name}"? This can't be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await settingsAPI.deleteListItem('note_templates', item.id);
      toast.success('Note template deleted');
      await fetchItems();
    } catch (err) {
      toast.error('Failed to delete note template');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const patch = { ...item, active: !item.active };
      await settingsAPI.updateListItem('note_templates', item.id, patch);
      await fetchItems();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const visible = hideInactive ? items.filter((i) => i.active !== false) : items;
  const inactiveCount = items.filter((i) => i.active === false).length;

  return (
    <Card className="mt-6">
      <SectionCardHeader
        title="Note Templates"
        description="Reusable notes for line items. Insert from the note field on any quote line."
        icon={DocumentTextIcon}
        actions={
          <Button onClick={() => setEditing(emptyNoteTemplate())} variant="secondary" size="sm">
            <PlusIcon className="h-4 w-4 mr-1" /> Add
          </Button>
        }
      />
      <CardBody>

      {error && <Alert variant="error" onClose={() => setError(null)} className="mb-3">{error}</Alert>}

      {/* Hide Inactive / Show All switch */}
      {inactiveCount > 0 && (
        <div className="flex items-center gap-2 mb-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          <button
            type="button"
            onClick={() => setHideInactive(!hideInactive)}
            className="underline"
            style={{ color: 'var(--color-action-primary)' }}
            aria-pressed={!hideInactive}
          >
            {hideInactive ? `Show all (${items.length})` : `Hide inactive (${inactiveCount})`}
          </button>
        </div>
      )}

      {loading ? (
        <div className="h-24 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
      ) : visible.length === 0 ? (
        <Card>
          <div className="px-6 py-8 text-center">
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {items.length === 0
                ? 'No note templates yet. Create one to reuse across your quote line items.'
                : `All note templates are inactive. Click "Show all (${items.length})" to see them.`}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((item) => (
            <NoteTemplateRow
              key={item.id}
              item={item}
              onEdit={() => setEditing(item)}
              onDelete={() => handleDelete(item)}
              onToggleActive={() => handleToggleActive(item)}
            />
          ))}
        </div>
      )}

      <NoteTemplateEditor
        open={!!editing}
        item={editing}
        onClose={() => setEditing(null)}
        onSave={handleSave}
      />
      </CardBody>
    </Card>
  );
}

function NoteTemplateRow({ item, onEdit, onDelete, onToggleActive }) {
  const inactive = item.active === false;
  return (
    <Card>
      <div className="flex items-start gap-3 p-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="font-medium text-sm"
              style={{
                color: inactive ? 'var(--color-text-muted)' : 'var(--color-text-default)',
              }}
            >
              {item.name}
            </span>
            {inactive && (
              <span
                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                style={{ backgroundColor: 'var(--neutral-100)', color: 'var(--color-text-muted)' }}
              >
                Inactive
              </span>
            )}
          </div>
          <p
            className="text-xs mt-1 whitespace-pre-wrap line-clamp-2"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {item.note}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <div className="mr-2">
            <Checkbox
              checked={!inactive}
              onChange={onToggleActive}
              label={<span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Active</span>}
              aria-label={`${item.name}: ${inactive ? 'activate' : 'deactivate'}`}
            />
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="p-1 rounded hover:bg-[color:var(--neutral-100)]"
            aria-label={`Edit ${item.name}`}
            title="Edit"
          >
            <PencilSquareIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1 rounded hover:bg-[color:var(--neutral-100)]"
            aria-label={`Delete ${item.name}`}
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" style={{ color: 'var(--color-danger)' }} />
          </button>
        </div>
      </div>
    </Card>
  );
}

function NoteTemplateEditor({ open, item, onClose, onSave }) {
  const [form, setForm] = useState(item);

  useEffect(() => { if (open) setForm(item); }, [open, item]);

  if (!open || !form) return null;

  const valid = form.name.trim() && form.note.trim();

  return (
    <Slideover
      open={open}
      onClose={onClose}
      title={item?.name ? 'Edit Note Template' : 'New Note Template'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!valid}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
            Name *
          </label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g., Spray-foam prep note"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>
            Note *
          </label>
          <Textarea
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="The template text. Users can edit the inserted text on a specific quote — editing there never edits this template."
            rows={6}
          />
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
            Both fields are required.
          </p>
        </div>
      </div>
    </Slideover>
  );
}
